import type { GoalItemRecord } from "./domain";

export function descendantLeafCompletion(
  rootId: string,
  items: GoalItemRecord[],
): boolean {
  const childrenByParent = new Map<string, GoalItemRecord[]>();
  for (const item of items) {
    if (!item.parentId) continue;
    const children = childrenByParent.get(item.parentId) ?? [];
    children.push(item);
    childrenByParent.set(item.parentId, children);
  }

  const root = items.find((item) => item.id === rootId);
  if (!root) return false;
  const rootChildren = childrenByParent.get(rootId) ?? [];
  if (rootChildren.length === 0) return root.completed;

  const visited = new Set<string>();
  const stack = [...rootChildren];
  let leafCount = 0;
  while (stack.length > 0) {
    const item = stack.pop();
    if (!item || visited.has(item.id)) return false;
    visited.add(item.id);
    const children = childrenByParent.get(item.id) ?? [];
    if (children.length === 0) {
      leafCount += 1;
      if (!item.completed) return false;
    } else {
      stack.push(...children);
    }
  }
  return leafCount > 0;
}

export function calculateScore(items: GoalItemRecord[]): number {
  const roots = items.filter((item) => item.parentId === null);
  return roots.reduce(
    (score, root) => score + (descendantLeafCompletion(root.id, items) ? 1 : 0),
    0,
  );
}

export type DailyScore = {
  logicalPeriodKey: string;
  score: number;
  closedAt: string;
};

export function calculateWeeklySummary(days: DailyScore[], todayKey: string) {
  const closed = days.filter((day) => day.logicalPeriodKey < todayKey);
  const total = closed.reduce((sum, day) => sum + day.score, 0);
  const best = closed.reduce<DailyScore | null>(
    (current, day) => (!current || day.score > current.score ? day : current),
    null,
  );
  return {
    total,
    denominator: closed.length * 10,
    average: closed.length ? total / closed.length : 0,
    completedThings: total,
    missedDays: closed.filter((day) => day.score === 0).length,
    bestDay: best,
    finalizedDays: closed.length,
  };
}

export function calculateStreak(scores: DailyScore[]): {
  current: number;
  best: number;
} {
  const ordered = [...scores].sort((a, b) =>
    a.logicalPeriodKey.localeCompare(b.logicalPeriodKey),
  );
  let current = 0;
  let best = 0;
  for (const day of ordered) {
    if (day.score > 0) {
      current += 1;
      best = Math.max(best, current);
    } else current = 0;
  }
  return { current, best };
}
